import { Box, Stack, Typography } from '@mui/material'
import { GRAY_800, WHITE } from 'constants/styling/theme'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { FC } from 'react'
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined'
import { MUITooltip } from 'components/common/MUITooltip'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import { useAdjustEditor } from './AdjustEditor.context'
import { useTranslation } from 'react-i18next'

const ZOOM_STEP = 1.25
const ZOOM_MAX = 8

const ZOOM_BUTTON_SX = {
  width: '3.2rem',
  height: '3.2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  borderRadius: '50%',
  background: 'none',
  color: GRAY_800,
  cursor: 'pointer',
  '&:disabled': { opacity: 0.35, cursor: 'default' },
  '& .MuiSvgIcon-root': { fontSize: '2rem' },
} as const

/** Zoom out · level · zoom in · fit — sits in the view controls row, on the right of Before | Compare | After */
export const AdjustZoomControls: FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { base, zoom, setZoom, pan, setPan } = useAdjustEditor()

  /** Zoom about the centre of the canvas */
  const zoomTo = (next: number) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(1, next))
    setPan(clamped === 1 ? { x: 0, y: 0 } : { x: pan.x * clamped / zoom, y: pan.y * clamped / zoom })
    setZoom(clamped)
  }

  return (
    <>
      {base && (
        <Stack flexDirection="row" alignItems="center" sx={{ padding: '0.2rem', borderRadius: '4rem', backgroundColor: WHITE, boxShadow: '0px 1px 4px 0px rgba(19, 24, 29, 0.1)' }}>

          <MUITooltip content={t('adjust.zoom.out')}>
            <Box component="button" type="button" aria-label={t('adjust.zoom.out')} disabled={zoom <= 1} onClick={() => zoomTo(zoom / ZOOM_STEP)} sx={ZOOM_BUTTON_SX}><RemoveRoundedIcon /></Box>
          </MUITooltip>

          <Typography sx={{ minWidth: '4.4rem', textAlign: 'center', fontSize: '1.2rem', fontVariantNumeric: 'tabular-nums', color: GRAY_800 }}>{Math.round(zoom * 100)}%</Typography>

          <MUITooltip content={t('adjust.zoom.in')}>
            <Box component="button" type="button" aria-label={t('adjust.zoom.in')} disabled={zoom >= ZOOM_MAX} onClick={() => zoomTo(zoom * ZOOM_STEP)} sx={ZOOM_BUTTON_SX}><AddRoundedIcon /></Box>
          </MUITooltip>

          <MUITooltip content={t('adjust.zoom.fit')}>
            <Box component="button" type="button" aria-label={t('adjust.zoom.fit')} disabled={zoom === 1} onClick={() => zoomTo(1)} sx={ZOOM_BUTTON_SX}><FitScreenOutlinedIcon /></Box>
          </MUITooltip>

        </Stack>
      )}
    </>
  )
}
